/**
 * Persona-Specific AI Assistant Configuration
 *
 * Visual chrome only — colours, gradients, icons, placeholders.
 * Greetings and opener buttons are NOT defined here. They are fetched
 * dynamically from POST /api/ai/suggestions so the assistant feels
 * conversational instead of replaying a hardcoded script.
 */

export type PersonaType = 'system_admin' | 'partner_admin' | 'company_admin' | 'compliance_user' | 'internal_auditor' | 'external_auditor';

export interface PersonaAssistantConfig {
  assistantName: string;
  assistantSubtitle: string;
  accentColor: string;           // Tailwind bg class for toggle button
  accentColorHex: string;        // Hex for non-Tailwind usage (shadows etc.)
  headerGradient: string;        // Tailwind gradient for chat header bg
  headerAccent: string;          // Tailwind text color for subtitle
  iconLabel: string;             // 2-char label for toggle button when collapsed
  inputPlaceholder: string;      // Placeholder text for the chat input
  toggleGlow: string;            // Tailwind shadow class for toggle button glow
  logoBg: string;                // Tailwind gradient for header logo
  emptyStateTitle: string;       // Title when no messages
  emptyStateSubtitle: string;    // Subtitle when no messages
}

export const PERSONA_CONFIGS: Record<PersonaType, PersonaAssistantConfig> = {
  system_admin: {
    assistantName: 'Yion Aegis',
    assistantSubtitle: 'Platform Command',
    accentColor: 'bg-indigo-600',
    accentColorHex: '#4f46e5',
    headerGradient: 'bg-gradient-to-r from-indigo-600 via-blue-600 to-sky-600',
    headerAccent: 'text-sky-200',
    logoBg: 'bg-gradient-to-br from-indigo-500 to-sky-600',
    iconLabel: 'A',
    toggleGlow: 'shadow-indigo-500/40',
    inputPlaceholder: 'Ask me anything about the platform...',
    emptyStateTitle: 'Platform Command Centre',
    emptyStateSubtitle: 'Full system access — companies, users, frameworks, billing, GDPR, and more',
    welcomeMessage: (name) =>
      `Hello ${name}! As your System Administration AI, I can help you manage companies, users, frameworks, system settings, compliance templates, GDPR modules, billing, and platform health. I have full access to all platform capabilities. What would you like to do?`,
    suggestions: [
      { label: 'Platform overview', value: 'Give me a platform overview — companies, users, frameworks, compliance status', icon: '📊' },
      { label: 'Create a company', value: 'I want to create a new company', icon: '🏢' },
      { label: 'Build a framework', value: 'Build a compliance framework', icon: '🔨' },
      { label: 'System health', value: 'Show me the system health status', icon: '💚' },
      { label: 'Manage users', value: 'Show me all users across the platform', icon: '👥' },
      { label: 'GDPR dashboard', value: 'Show me the GDPR compliance dashboard', icon: '🛡️' },
      { label: 'IoT device management', value: 'Show me all registered IoT devices and their status', icon: '📡' },
      { label: 'Security dashboard', value: 'Show me the security dashboard — active sessions, failed logins, anomalies', icon: '🔒' },
      { label: 'Training programmes', value: 'Show me training modules and completion rates across the platform', icon: '📚' },
      { label: 'Regulatory monitoring', value: 'Set up regulatory change monitoring for active frameworks', icon: '⚖️' },
      { label: 'Billing & revenue', value: 'Show me billing metrics, subscription plans, and revenue overview', icon: '💰' },
      { label: 'AI capabilities', value: 'How many AI capabilities are available and what can I do?', icon: '🤖' },
    ],
  },

  partner_admin: {
    assistantName: 'Yion Atlas',
    assistantSubtitle: 'Portfolio Command',
    accentColor: 'bg-emerald-600',
    accentColorHex: '#059669',
    headerGradient: 'bg-gradient-to-r from-emerald-600 via-teal-600 to-cyan-600',
    headerAccent: 'text-emerald-100',
    logoBg: 'bg-gradient-to-br from-emerald-500 to-cyan-600',
    iconLabel: 'A',
    toggleGlow: 'shadow-emerald-500/40',
    inputPlaceholder: 'Ask about your portfolio companies, compliance status, or frameworks...',
    emptyStateTitle: 'Portfolio Intelligence',
    emptyStateSubtitle: 'Manage your client portfolio, track compliance across companies, and monitor frameworks',
    welcomeMessage: (name) =>
      `Hello ${name}! I'm your Portfolio Intelligence assistant. I can help you monitor compliance across your client companies, track framework progress, manage your portfolio, view carbon credits, and generate cross-company reports. What would you like to explore?`,
    suggestions: [
      { label: 'Portfolio overview', value: 'Give me an overview of my portfolio — companies, compliance status, and framework progress', icon: '📊' },
      { label: 'Company compliance status', value: 'Show me compliance status across all my portfolio companies', icon: '✅' },
      { label: 'Add a client company', value: 'I want to add a new client company to my portfolio', icon: '🏢' },
      { label: 'Framework progress', value: 'Show framework completion progress across my portfolio', icon: '📈' },
      { label: 'Carbon credits', value: 'Show me carbon credit status across my portfolio companies', icon: '🌿' },
      { label: 'At-risk companies', value: 'Which of my client companies have compliance gaps or overdue tasks?', icon: '⚠️' },
      { label: 'Revenue dashboard', value: 'Show me my revenue from client companies and Stripe Connect earnings', icon: '💰' },
      { label: 'Client certifications', value: 'Show certification status and renewals across my portfolio', icon: '🏆' },
      { label: 'Training compliance', value: 'Show training completion rates across all client companies', icon: '📚' },
      { label: 'Regulatory alerts', value: 'Are there any regulatory changes affecting my clients?', icon: '⚖️' },
      { label: 'Supply chain overview', value: 'Show supply chain emissions across my portfolio companies', icon: '🔗' },
      { label: 'Client benchmarking', value: 'Benchmark my client companies against industry averages', icon: '📈' },
    ],
  },

  company_admin: {
    assistantName: 'Yion Forge',
    assistantSubtitle: 'Company Command',
    accentColor: 'bg-orange-600',
    accentColorHex: '#ea580c',
    headerGradient: 'bg-gradient-to-r from-amber-500 via-orange-500 to-rose-500',
    headerAccent: 'text-orange-100',
    logoBg: 'bg-gradient-to-br from-amber-500 to-rose-600',
    iconLabel: 'F',
    toggleGlow: 'shadow-orange-500/40',
    inputPlaceholder: 'Enter compliance directive...',
    emptyStateTitle: 'Compliance Operations Center',
    emptyStateSubtitle: 'Centralized compliance management and operational oversight',
    welcomeMessage: (name) =>
      `Director ${name}, welcome to the Compliance Operations Center. I am your enterprise compliance command system. I can manage team operations, monitor compliance metrics, review evidence submissions, coordinate risk assessments, and generate operational reports. What directive shall I execute?`,
    suggestions: [
      { label: 'Operational status report', value: 'Generate operational status report for team compliance activities', icon: '📈' },
      { label: 'Task assignment protocol', value: 'Execute task assignment protocol for compliance operations', icon: '⚙️' },
      { label: 'Evidence review queue', value: 'Access evidence review queue for operational approval', icon: '🔍' },
      { label: 'Personnel operations roster', value: 'Display personnel operations roster with compliance roles', icon: '👥' },
      { label: 'Risk assessment matrix', value: 'Execute risk assessment matrix analysis', icon: '⚠️' },
      { label: 'Inter-team communication', value: 'Initiate inter-team communication protocol', icon: '📡' },
      { label: 'Framework deployment', value: 'Deploy compliance framework across operations', icon: '🔧' },
      { label: 'Executive operations summary', value: 'Generate executive operations summary report', icon: '📊' },
      { label: 'IoT monitoring', value: 'Show IoT device status and emissions data collection', icon: '📡' },
      { label: 'Training management', value: 'Create and assign training modules to my team', icon: '📚' },
      { label: 'Supply chain risk', value: 'Create supply chain emissions map and risk assessment', icon: '🔗' },
      { label: 'Regulatory monitoring', value: 'Set up alerts for regulatory changes affecting my company', icon: '⚖️' },
      { label: 'Emissions intelligence', value: 'Analyse Scope 3 hotspots and SBTi pathway alignment', icon: '🌿' },
      { label: 'Trust portal setup', value: 'Configure trust portal for secure supplier data sharing', icon: '🤝' },
      { label: 'Workflow automation', value: 'Create approval workflows and task automation rules', icon: '⚙️' },
      { label: 'Certification readiness', value: 'Check certification prerequisites and gap analysis', icon: '🏆' },
    ],
  },

  compliance_user: {
    assistantName: 'Yion Sentinel',
    assistantSubtitle: 'Compliance Execution',
    accentColor: 'bg-violet-600',
    accentColorHex: '#7c3aed',
    headerGradient: 'bg-gradient-to-r from-violet-600 via-purple-600 to-fuchsia-600',
    headerAccent: 'text-violet-100',
    logoBg: 'bg-gradient-to-br from-violet-500 to-fuchsia-600',
    iconLabel: 'S',
    toggleGlow: 'shadow-violet-500/40',
    inputPlaceholder: 'Ask about your tasks, evidence, or compliance pages...',
    emptyStateTitle: 'Compliance Guide',
    emptyStateSubtitle: 'Understand your tasks, prepare evidence, and complete compliance pages',
    welcomeMessage: (name) =>
      `Hello ${name}! I'm your Compliance Guide. I can help you understand your assigned tasks, prepare the right evidence, check deadlines, and complete compliance pages. Let me know what you need help with.`,
    suggestions: [
      { label: 'My assigned tasks', value: 'Show me my assigned compliance tasks and deadlines', icon: '✅' },
      { label: 'What evidence do I need?', value: 'What evidence do I need to complete my current tasks?', icon: '📎' },
      { label: 'Upload evidence guide', value: 'Guide me through uploading evidence for a compliance page', icon: '📤' },
      { label: 'My progress', value: 'What\'s my compliance completion progress?', icon: '📊' },
      { label: 'Understand a requirement', value: 'Help me understand what a compliance requirement is asking for', icon: '💡' },
      { label: 'Message my admin', value: 'I need to send a message to my company admin', icon: '💬' },
      { label: 'My training modules', value: 'Show my assigned training modules and completion status', icon: '📚' },
      { label: 'Evidence suggestions', value: 'What evidence should I prepare for my current tasks?', icon: '💡' },
    ],
  },

  internal_auditor: {
    assistantName: 'Yion Veritas',
    assistantSubtitle: 'Internal Audit',
    accentColor: 'bg-slate-700',
    accentColorHex: '#334155',
    headerGradient: 'bg-gradient-to-r from-slate-700 via-slate-600 to-zinc-600',
    headerAccent: 'text-slate-200',
    logoBg: 'bg-gradient-to-br from-slate-500 to-zinc-700',
    iconLabel: 'V',
    toggleGlow: 'shadow-slate-500/40',
    inputPlaceholder: 'Ask about evidence reviews, audit readiness, or findings...',
    emptyStateTitle: 'Audit Intelligence',
    emptyStateSubtitle: 'Review evidence, assess audit readiness, manage non-conformities, and generate reports',
    welcomeMessage: (name) =>
      `Hello ${name}! I'm your Audit Intelligence assistant. I can help you review submitted evidence, check audit readiness, manage non-conformities, track CAPAs, and generate audit summaries. What would you like to review?`,
    suggestions: [
      { label: 'Pages ready for review', value: 'Show me compliance pages ready for audit review', icon: '📋' },
      { label: 'Evidence quality check', value: 'Help me assess the quality of submitted evidence', icon: '🔍' },
      { label: 'Non-conformities', value: 'Show current non-conformities and their status', icon: '🚩' },
      { label: 'CAPA status', value: 'What is the current CAPA status?', icon: '🔧' },
      { label: 'Audit summary report', value: 'Generate an audit summary report', icon: '📄' },
      { label: 'Compliance gaps', value: 'Analyse compliance gaps across frameworks', icon: '📉' },
      { label: 'Audit planning', value: 'Help me create an audit plan with scope and schedule', icon: '📋' },
      { label: 'Certification check', value: 'Check certification prerequisites before external audit', icon: '🏆' },
      { label: 'Anomaly detection', value: 'Show me any anomalies detected in recent submissions', icon: '🔍' },
      { label: 'Management review', value: 'Help me assess management review effectiveness', icon: '📊' },
    ],
  },

  external_auditor: {
    assistantName: 'Yion Apex',
    assistantSubtitle: 'Certification Command',
    accentColor: 'bg-rose-600',
    accentColorHex: '#e11d48',
    headerGradient: 'bg-gradient-to-r from-rose-600 via-red-600 to-orange-600',
    headerAccent: 'text-rose-100',
    logoBg: 'bg-gradient-to-br from-rose-500 to-orange-600',
    iconLabel: 'A',
    toggleGlow: 'shadow-rose-500/40',
    inputPlaceholder: 'Ask about certification readiness, evidence, or audit packs...',
    emptyStateTitle: 'Certification Assistant',
    emptyStateSubtitle: 'Review verified evidence, assess certification readiness, and manage the certification workflow',
    welcomeMessage: (name) =>
      `Hello ${name}! I'm your Certification Assistant. I can help you review internally verified evidence, assess certification readiness, manage the certification workflow, and export audit packs. All data is presented in blind audit mode. What would you like to examine?`,
    suggestions: [
      { label: 'Certification readiness', value: 'Check certification readiness for assigned frameworks', icon: '🏆' },
      { label: 'Review verified evidence', value: 'Show me internally verified evidence for review', icon: '✅' },
      { label: 'Export audit pack', value: 'Generate an audit pack for the current framework', icon: '📦' },
      { label: 'Certification status', value: 'Show current certification status and history', icon: '📜' },
      { label: 'Surveillance audits', value: 'Are there any surveillance audits due?', icon: '🔔' },
      { label: 'Gap analysis', value: 'Run certification gap analysis for the current framework', icon: '📉' },
      { label: 'SBTi alignment', value: 'Check SBTi pathway alignment for this organisation', icon: '🎯' },
      { label: 'Surveillance planning', value: 'Help me plan surveillance audits for active certifications', icon: '🔔' },
    ],
  },
};

/**
 * Resolve persona from user permissions and company context.
 * MUST match server-side CompliancePersonaResolver exactly.
 * Order: system_admin → partner_admin → company_admin → internal_auditor → external_auditor → compliance_user
 * System admin MUST be checked FIRST because they also have partner permissions.
 */
export function resolveClientPersona(
  permissions: string[] | undefined,
  companyId: number | null | undefined,
  partnerId?: number | null
): PersonaType {
  if (!permissions || permissions.length === 0) return 'compliance_user';

  const has = (p: string) => permissions.includes(p);

  // 1. System admin: no company + system-level permissions (CHECK FIRST — highest privilege)
  // System admins also have partner.dashboard.view, so this MUST come before partner_admin
  if (!companyId && has('companies.create')) return 'system_admin';

  // 2. Partner admin: no company, has partner-specific permission (but NOT system admin)
  if (!companyId && (has('partner.dashboard.view') || has('partners.manage'))) return 'partner_admin';

  // 2. Company admin: has company affiliation + companies.update
  if (companyId && has('companies.update')) return 'company_admin';

  // 3. Internal auditor: has audit.internal permission
  if (has('audit.internal')) return 'internal_auditor';

  // 4. External auditor: has audit.external permission
  if (has('audit.external')) return 'external_auditor';

  // Fallback: check dashboard permissions for demo users
  if (companyId && has('audit.dashboard.view') && !has('company.dashboard.view')) return 'internal_auditor';
  if (!companyId && has('audit.dashboard.view')) return 'external_auditor';
  if (companyId && has('company.dashboard.view')) return 'company_admin';

  // 5. Compliance user: has any compliance.* permission
  if (permissions.some(p => p.startsWith('compliance.'))) return 'compliance_user';

  // Fallback
  return 'compliance_user';
}
